// Solo / isolate-playback helpers.
//
// Solo is a UI-only inspection mode — it does not mutate the Song. The
// play pipeline calls `isolateForSolo(song, id)` to build a reduced
// Song that contains only what's needed to hear the soloed block in
// isolation: the soloed subtree, its transitive `const` dependencies,
// and any timing statements (`setcpm` / `setcps`).
//
// `computeSoloVisibility` returns the set of block ids that should
// render un-dimmed: the soloed block, everything in its subtree
// ("descendants play too"), and its ancestors ("structural context").

import { isConditionalEffect } from './types';
import type {
  Effect,
  Expression,
  Song,
  Statement,
  BlockId,
} from './types';
import { newBlockId } from './types';

// ─── Public API ─────────────────────────────────────────────────────

/** Build a reduced Song containing only the soloed subtree plus the
 *  `const` deps and timing statements needed to make it play. Returns
 *  the original song unchanged if `soloId` doesn't match anything the
 *  block editor can solo. */
export function isolateForSolo(song: Song, soloId: BlockId): Song {
  const target = findSoloTarget(song, soloId);
  if (!target) return song;

  // Preamble: tempo + sample-pack loaders. Both must survive isolation
  // so the soloed expression still has a tempo and any custom samples
  // it references are still loaded.
  const timing: Statement[] = song.statements.filter(
    (s) => s.kind === 'setcpm' || s.kind === 'setcps' || s.kind === 'samples',
  );

  const constByName = new Map<
    string,
    Extract<Statement, { kind: 'const' }>
  >();
  for (const stmt of song.statements) {
    if (stmt.kind === 'const') constByName.set(stmt.name, stmt);
  }

  // Transitive closure of const dependencies, starting from the play
  // expression and excluding the soloed const itself (if any).
  const resolved = new Set<string>();
  const queue: Expression[] = [target.playExpr];
  while (queue.length > 0) {
    const expr = queue.shift()!;
    collectRefNames(expr).forEach((name) => {
      if (name === target.skipConstName) return;
      if (resolved.has(name)) return;
      const def = constByName.get(name);
      if (!def) return;
      resolved.add(name);
      queue.push(def.expr);
    });
  }

  // Preserve the original source order of the surviving consts so ref
  // resolution still works (Strudel evaluates top-to-bottom).
  const keptConsts: Statement[] = [];
  for (const stmt of song.statements) {
    if (stmt.kind === 'const' && resolved.has(stmt.name)) {
      keptConsts.push(stmt);
    }
  }

  const playStmt: Statement = {
    id: newBlockId(),
    kind: 'expression',
    expr: target.playExpr,
  };

  return {
    statements: [...timing, ...keptConsts, playStmt],
  };
}

/** Set of block ids that should render un-dimmed when a solo is active:
 *  the soloed block, its subtree, and its ancestor chain. Returns an
 *  empty set if the id isn't found — callers treat an empty set as
 *  "no dim" (graceful fallback for stale ids). */
export function computeSoloVisibility(
  song: Song,
  soloId: BlockId,
): Set<BlockId> {
  const visible = new Set<BlockId>();
  const path = findPathToBlock(song, soloId);
  if (!path) return visible;
  for (const id of path) visible.add(id);
  // Add descendants of the soloed block. The terminal node of `path`
  // is the soloed block itself.
  const soloedBlock = findAnyById(song, soloId);
  if (soloedBlock) collectDescendantIds(soloedBlock, visible);
  // Preamble (setcpm / setcps / samples) always survives isolation —
  // keep visually bright so they don't read as muted.
  for (const stmt of song.statements) {
    if (
      stmt.kind === 'setcpm' ||
      stmt.kind === 'setcps' ||
      stmt.kind === 'samples'
    ) {
      visible.add(stmt.id);
    }
  }
  return visible;
}

/** True if the soloed id still exists anywhere in the song. Used by
 *  useSongEdit to auto-clear a stale solo after the song reparses. */
export function soloIdStillExists(song: Song, soloId: BlockId): boolean {
  return findAnyById(song, soloId) !== null;
}

/** True if the given id is a valid solo target — a top-level const,
 *  a top-level expression statement, or any Expression in the tree.
 *  Effect ids and arrange-entry ids are NOT soloable. Used to reject
 *  stale/invalid solo requests (e.g., the keyboard shortcut landing
 *  on a focused effect row). */
export function isSoloableId(song: Song, id: BlockId): boolean {
  return findSoloTarget(song, id) !== null;
}

// ─── Solo target resolution ─────────────────────────────────────────

type SoloTarget = {
  /** The expression to wrap as a top-level `expression` statement. */
  playExpr: Expression;
  /** When the soloed block is a `const` statement, its own name — so
   *  we don't pull it back in via the dep closure. */
  skipConstName?: string;
};

function findSoloTarget(song: Song, soloId: BlockId): SoloTarget | null {
  for (const stmt of song.statements) {
    if (stmt.id === soloId) {
      if (stmt.kind === 'const') {
        return { playExpr: stmt.expr, skipConstName: stmt.name };
      }
      if (stmt.kind === 'expression') {
        return { playExpr: stmt.expr };
      }
      // setcpm / setcps / comment — not soloable.
      return null;
    }
    if (stmt.kind === 'const' || stmt.kind === 'expression') {
      const found = findExpressionById(stmt.expr, soloId);
      if (found) return { playExpr: found };
    }
  }
  return null;
}

function findExpressionById(
  expr: Expression,
  id: BlockId,
): Expression | null {
  if (expr.id === id) return expr;
  if (expr.kind === 'stack') {
    for (const child of expr.children) {
      const found = findExpressionById(child, id);
      if (found) return found;
    }
  } else if (expr.kind === 'arrange') {
    for (const entry of expr.entries) {
      const found = findExpressionById(entry.section, id);
      if (found) return found;
    }
  }
  return null;
}

// ─── Ref-name collection ────────────────────────────────────────────

function collectRefNames(expr: Expression): string[] {
  const names: string[] = [];
  walkRefs(expr, (name) => {
    names.push(name);
  });
  return names;
}

function walkRefs(expr: Expression, visit: (name: string) => void): void {
  switch (expr.kind) {
    case 'ref':
      visit(expr.name);
      return;
    case 'stack':
      for (const c of expr.children) walkRefs(c, visit);
      return;
    case 'arrange':
      for (const e of expr.entries) walkRefs(e.section, visit);
      return;
    case 'voice.s':
    case 'voice.note':
    case 'customFunction':
      // Voices hold patterns, not refs; custom functions are opaque
      // source text. Neither contributes tracked ref dependencies.
      return;
  }
}

// ─── Id walks for visibility ────────────────────────────────────────

/** Resolve an id to its owning block. `null` if the id isn't anywhere
 *  soloable in the tree. Effect ids and arrange-entry ids are not
 *  tracked — neither is a solo target, and their survival is covered
 *  by their containing expression. */
function findAnyById(song: Song, id: BlockId): Statement | Expression | null {
  for (const stmt of song.statements) {
    if (stmt.id === id) return stmt;
    if (stmt.kind === 'const' || stmt.kind === 'expression') {
      const found = findExpressionById(stmt.expr, id);
      if (found) return found;
    }
  }
  return null;
}

/** Returns the list of block ids forming the path from the containing
 *  top-level statement down to (and including) the target id. `null`
 *  if the id isn't found. */
function findPathToBlock(song: Song, targetId: BlockId): BlockId[] | null {
  for (const stmt of song.statements) {
    if (stmt.id === targetId) return [stmt.id];
    if (stmt.kind === 'const' || stmt.kind === 'expression') {
      const sub = findPathInExpression(stmt.expr, targetId);
      if (sub) return [stmt.id, ...sub];
    }
  }
  return null;
}

function findPathInExpression(
  expr: Expression,
  targetId: BlockId,
): BlockId[] | null {
  if (expr.id === targetId) return [expr.id];
  if (expr.kind === 'stack') {
    for (const child of expr.children) {
      const sub = findPathInExpression(child, targetId);
      if (sub) return [expr.id, ...sub];
    }
  } else if (expr.kind === 'arrange') {
    for (const entry of expr.entries) {
      if (entry.id === targetId) return [expr.id, entry.id];
      const sub = findPathInExpression(entry.section, targetId);
      if (sub) return [expr.id, entry.id, ...sub];
    }
  }
  return null;
}

/** Collects every id in the subtree rooted at `block` into `into`,
 *  including the block itself. Effect ids on voices/refs are included
 *  so dimming a voice also keeps its effect rows visible. */
function collectDescendantIds(
  block: Statement | Expression,
  into: Set<BlockId>,
): void {
  into.add(block.id);
  if (isStatement(block)) {
    if (block.kind === 'const' || block.kind === 'expression') {
      collectExpressionIds(block.expr, into);
    }
    // setcpm / setcps / comment — no subtree.
    return;
  }
  collectExpressionIds(block, into);
}

function isStatement(block: Statement | Expression): block is Statement {
  return (
    block.kind === 'const' ||
    block.kind === 'expression' ||
    block.kind === 'setcpm' ||
    block.kind === 'setcps' ||
    block.kind === 'comment'
  );
}

function collectExpressionIds(expr: Expression, into: Set<BlockId>): void {
  into.add(expr.id);
  if (expr.kind === 'voice.s' || expr.kind === 'voice.note') {
    for (const eff of expr.effects) collectEffectIds(eff, into);
  } else if (expr.kind === 'ref') {
    for (const eff of expr.effects ?? []) collectEffectIds(eff, into);
  } else if (expr.kind === 'stack') {
    for (const c of expr.children) collectExpressionIds(c, into);
  } else if (expr.kind === 'arrange') {
    for (const entry of expr.entries) {
      into.add(entry.id);
      collectExpressionIds(entry.section, into);
    }
  }
}

function collectEffectIds(effect: Effect, into: Set<BlockId>): void {
  into.add(effect.id);
  if (isConditionalEffect(effect)) {
    for (const inner of effect.body) collectEffectIds(inner, into);
  }
}
